import { createContext, useContext, useState } from "react";

const PrivacyModeContext = createContext<{
  privacyModeEnabled: boolean;
  setPrivacyMode: (value: boolean) => void;
  togglePrivacyMode: () => void;
}>({
  privacyModeEnabled: false,
  setPrivacyMode: () => {},
  togglePrivacyMode: () => {},
});

export const PrivacyModeProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [privacyModeEnabled, setPrivacyModeEnabled] = useState(() => {
    return localStorage.getItem("privacyMode") === "on";
  });

  // Function to update privacy mode
  const setPrivacyMode = (enabled: boolean) => {
    localStorage.setItem("privacyMode", enabled ? "on" : "off");
    setPrivacyModeEnabled(enabled);
  };

  const togglePrivacyMode = () => {
    const newMode = !privacyModeEnabled;
    setPrivacyMode(newMode);
  };

  return (
    <PrivacyModeContext.Provider
      value={{
        privacyModeEnabled,
        setPrivacyMode,
        togglePrivacyMode,
      }}
    >
      {children}
    </PrivacyModeContext.Provider>
  );
};

export const usePrivacyMode = () => {
  const context = useContext(PrivacyModeContext);
  if (!context) {
    throw new Error("usePrivacyMode must be used within a PrivacyModeProvider");
  }
  return context;
};

// Array of fake company names
const FAKE_COMPANY_NAMES = [
  // Hotels & Resorts
  "Grand Meridian Hotels",
  "Sunset Harbor Resort Group",
  "Golden Gate Hospitality",
  "Crescent Moon Hotels",
  "Azure Coast Resorts",
  "Pinnacle Lodge Collection",
  "Riverstone Inn & Suites",
  "Starlight Hotel Group",
  "Grandview Resort Partners",
  "Silver Springs Hospitality",
  "Mountain Peak Hotels",
  "Oceanview Resort Company",
  "Crystal Bay Lodging",
  "Royal Crown Hotels",
  "Emerald Valley Resorts",
  "Platinum Crest Hospitality",
  "Serenity Shores Hotels",
  "Diamond Ridge Resort Group",
  "Sapphire Coast Inns",
  "Liberty Bell Hotel Company",
  "Cascade Falls Resorts",
  "Majestic Vista Hotels",
  "Harmony Hill Lodging",
  "Paradise Point Hospitality",
  "Sunrise Summit Hotels",

  // Bars & Taverns
  "The Rusty Anchor Pub",
  "Moonlight Tavern Co.",
  "Copper Kettle Bar Group",
  "The Golden Horseshoe",
  "Fireside Tavern Company",
  "The Silver Dollar Saloon",
  "Blackstone Pub & Grill",
  "The Wooden Barrel Tavern",
  "Iron Horse Bar Company",
  "The Laughing Griffin Pub",
  "Stormcrow Tavern Group",
  "The Dancing Bear Inn",
  "Cobblestone Pub Co.",
  "The Crimson Rose Tavern",
  "Wayward Wanderer Pubs",
  "The Blue Boar Taphouse",
  "Thornfield Tavern Company",
  "The Prancing Pony Inn",
  "Maplewood Pub Group",
  "The King's Head Tavern",
  "Rosewood Bar & Grill",
  "The White Hart Inn",
  "Millstone Tavern Co.",
  "The Red Lion Pub",
  "Oakwood Bar Company",

  // Restaurant Groups
  "Harbor View Dining Group",
  "Bistro Boulevard Company",
  "Garden Gate Restaurants",
  "Cornerstone Café Group",
  "Artisan Kitchen Company",
  "Farm Table Restaurant Group",
  "Metropolitan Dining Co.",
  "Harvest Moon Restaurants",
  "Riverside Bistro Group",
  "The Culinary Collective",
  "Stonehearth Restaurant Co.",
  "Wildflower Dining Group",
  "Copper Pot Restaurant Company",
  "The Greenhouse Café Group",
  "Vintage Vine Restaurants",
  "Heritage Kitchen Company",
  "Crossroads Dining Group",
  "The Local Table Co.",
  "Seaside Grill Company",
  "The Urban Plate Group",

  // Entertainment & Nightlife
  "Neon Nights Entertainment",
  "The Velvet Room Group",
  "Starlight Entertainment Co.",
  "Club Midnight Holdings",
  "The Phoenix Lounge Company",
  "Electric Avenue Entertainment",
  "The Underground Club Group",
  "Spotlight Entertainment Co.",
  "The Jazz Corner Company",
  "Rhythm & Blues Entertainment",
  "The Speakeasy Group",
  "Nightfall Entertainment Co.",
  "The Ivory Lounge Company",
  "Crimson Nightclub Group",
  "The Platinum Club Co.",
  "Velour Entertainment Group",
  "The Sapphire Lounge Co.",
  "Midnight Moon Entertainment",
  "The Ruby Room Group",
  "Crystal Club Company",

  // Event & Catering
  "Grandeur Events Company",
  "Celebration Central Group",
  "Premier Party Planners",
  "Elegant Affairs Company",
  "The Event Artisans",
  "Milestone Moments Group",
  "Perfect Day Event Co.",
  "Signature Celebrations",
  "The Gathering Place Company",
  "Festive Occasions Group",
  "Dream Day Event Planners",
  "The Social Circle Company",
  "Memorable Moments Group",
  "Enchanted Events Co.",
  "The Party Professionals",
  "Blissful Celebrations",
  "The Occasion Company",
  "Sterling Event Group",
  "Majestic Moments Co.",
  "The Festivity Company",
];

// Simple hash function to convert string to number
const simpleHash = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
};

export const fakeCompanyNameFromCompanyName = (companyName: string): string => {
  // Normalize the input to ensure consistency
  const normalizedName = companyName.toLowerCase().trim();

  // Generate a hash and use it to select from our array
  const hash = simpleHash(normalizedName);
  const index = hash % FAKE_COMPANY_NAMES.length;

  return FAKE_COMPANY_NAMES[index];
};

const FAKE_EMPLOYEE_NAMES = [
  "Alex Johnson",
  "Sarah Martinez",
  "Michael Chen",
  "Emily Rodriguez",
  "David Thompson",
  "Jessica Williams",
  "Robert Anderson",
  "Amanda Davis",
  "Christopher Lee",
  "Lauren Wilson",
  "Matthew Brown",
  "Rachel Garcia",
  "Daniel Miller",
  "Ashley Moore",
  "Kevin Taylor",
  "Stephanie White",
  "Brian Jackson",
  "Nicole Harris",
  "Andrew Clark",
  "Megan Lewis",
  "Joshua Walker",
  "Samantha Hall",
  "Ryan Young",
  "Elizabeth Allen",
  "Tyler King",
  "Victoria Wright",
  "Justin Lopez",
  "Kimberly Hill",
  "Brandon Scott",
  "Catherine Green",
  "Jordan Adams",
  "Natalie Baker",
  "Nicholas Nelson",
  "Rebecca Carter",
  "Anthony Mitchell",
  "Hannah Perez",
  "Jacob Roberts",
  "Olivia Turner",
  "William Phillips",
  "Grace Campbell",
  "Ethan Parker",
  "Sophie Evans",
  "Alexander Edwards",
  "Emma Collins",
  "Noah Stewart",
  "Chloe Sanchez",
  "Logan Morris",
  "Abigail Reed",
  "Lucas Cook",
  "Madison Bailey",
  "Mason Rivera",
  "Isabella Cooper",
  "James Richardson",
  "Aria Cox",
  "Benjamin Ward",
  "Zoe Torres",
  "Henry Peterson",
  "Lily Gray",
  "Sebastian Ramirez",
  "Maya James",
  "Jack Watson",
  "Avery Brooks",
  "Owen Kelly",
  "Ella Sanders",
  "Carter Price",
  "Scarlett Bennett",
  "Wyatt Wood",
  "Hazel Barnes",
  "Luke Ross",
  "Nora Henderson",
  "Gabriel Coleman",
  "Layla Jenkins",
  "Isaac Perry",
  "Penelope Powell",
  "Julian Long",
  "Aurora Patterson",
  "Levi Hughes",
  "Ruby Flores",
  "Adrian Washington",
  "Violet Butler",
  "Caleb Simmons",
  "Savannah Foster",
  "Ian Gonzales",
  "Paisley Bryant",
  "Connor Alexander",
  "Skylar Russell",
  "Nathaniel Griffin",
  "Delilah Diaz",
  "Hunter Hayes",
  "Aubrey Myers",
  "Christian Ford",
  "Claire Hamilton",
  "Thomas Graham",
  "Audrey Sullivan",
  "Blake Wallace",
  "Anna Woods",
  "Parker Fleming",
  "Leah Washington",
  "Dominic West",
  "Caroline Jordan",
  "Gavin Palmer",
  "Alexa Fisher",
  "Tristan Murray",
  "Piper Hawkins",
  "Brayden Stone",
  "Mackenzie Cole",
];

export const fakeAssigneeNameForAssignee = (assignee: string): string => {
  if (assignee === "Unassigned") {
    return assignee;
  }

  const normalizedName = assignee.toLowerCase().trim();

  // Generate a hash and use it to select from our array
  const hash = simpleHash(normalizedName);
  const index = hash % FAKE_EMPLOYEE_NAMES.length;

  return FAKE_EMPLOYEE_NAMES[index];
};
